import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { USECASES, BREITSCHEIDPLATZ_LINK, MOTHER_KEY } from "@/lib/usecases";
import Footer from "@/components/berlin/Footer";
import { BrandWordmark } from "@/components/berlin/BrandWordmark";
import { ArrowUpRight, ArrowLeft, Plus, Check } from "lucide-react";

const BASE = "https://berlin-cube.de";
const ease = [0.22, 1, 0.36, 1];
const h2Cls = "font-display font-light tracking-[-0.02em] leading-[1.1] text-white";
const h2Size = { fontSize: "clamp(1.5rem, 2.6vw, 2.3rem)" };
const pathFor = (key, lang) => {
  const u = USECASES.find((o) => o.key === key);
  return lang === "en" ? `/en/${u.enSlug}` : `/${u.deSlug}`;
};

const CompareSection = ({ s }) => (
  <div>
    <h2 className={h2Cls} style={h2Size}>{s.h2}</h2>
    {s.body && <p className="mt-5 text-white/65 leading-[1.7] text-[15px] md:text-base">{s.body}</p>}
    <div className="mt-10 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10" data-testid="usecase-compare">
      {[s.left, s.right].map((col, ci) => (
        <div key={ci} className={`p-7 md:p-9 ${ci === 1 ? "bg-[#16191D]" : "bg-[#0F1114]"}`}>
          <div className={`text-[11px] tracking-[0.35em] uppercase font-display ${ci === 1 ? "text-[#F5D77A]" : "text-white/45"}`}>{col.title}</div>
          {col.note && <p className="mt-3 text-[13px] leading-relaxed text-white/45">{col.note}</p>}
          <ul className="mt-6 space-y-3">
            {col.items.map((it) => (
              <li key={it} className="flex items-start gap-3 text-white/80 text-[15px] leading-snug">
                <Check className={`mt-0.5 h-4 w-4 shrink-0 ${ci === 1 ? "text-[#F5D77A]" : "text-white/40"}`} strokeWidth={1.5} />
                {it}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

const AudiencesSection = ({ s, lang }) => (
  <div>
    <h2 className={h2Cls} style={h2Size}>{s.h2}</h2>
    <div className="mt-8 divide-y divide-white/10 border-y border-white/10" data-testid="usecase-audiences">
      {s.items.map((it) => {
        const to = it.key ? pathFor(it.key, lang) : it.href;
        const inner = (
          <>
            <div className="md:w-72 shrink-0 font-display font-light text-lg md:text-xl tracking-[-0.01em] text-white group-hover:text-[#F5D77A] transition-colors">{it.t}</div>
            <p className="flex-1 text-white/65 leading-[1.7] text-[15px] md:text-base">{it.d}</p>
            {to && <ArrowUpRight className="hidden md:block mt-1.5 h-4 w-4 shrink-0 text-white/40 group-hover:text-[#F5D77A] transition-colors" strokeWidth={1.5} />}
          </>
        );
        const cls = "group flex flex-col md:flex-row gap-2 md:gap-8 py-6";
        return to ? (
          <Link key={it.t} to={to} className={cls} data-testid={`usecase-audience-${it.key || "link"}`}>{inner}</Link>
        ) : (
          <div key={it.t} className={cls}>{inner}</div>
        );
      })}
    </div>
  </div>
);

const ProofSection = ({ s }) => (
  <div data-testid="usecase-proof">
    <h2 className={h2Cls} style={h2Size}>{s.h2}</h2>
    {s.body && <p className="mt-5 text-white/65 leading-[1.7] text-[15px] md:text-base">{s.body}</p>}
    <dl className="mt-8 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
      {s.facts.map((f, i) => (
        <div key={f.k} className={`bg-[#0F1114] p-5 md:p-6 ${i === s.facts.length - 1 && s.facts.length % 2 ? "sm:col-span-2" : ""}`}>
          <dt className="text-[11px] tracking-[0.3em] uppercase text-white/40 font-display">{f.k}</dt>
          <dd className="mt-2 font-display font-light text-lg md:text-xl text-white tracking-[-0.01em]">{f.v}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

const Badge = ({ type }) => {
  const { lang } = useLang();
  const txt =
    type === "vorfuehrung"
      ? lang === "de" ? "Vorführung · Foto" : "Demonstration · Photo"
      : lang === "de" ? "Heute · Foto" : "Today · Photo";
  return (
    <div className="absolute top-3 left-3 md:top-4 md:left-4 z-10 inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-white/85 bg-black/50 backdrop-blur px-3 py-1.5 border border-white/10 font-display">
      <span className="h-1 w-1 rounded-full bg-white/70" />
      {txt}
    </div>
  );
};

const setLinkTag = (rel, hreflang, href) => {
  let sel = `link[rel="${rel}"]` + (hreflang ? `[hreflang="${hreflang}"]` : "");
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export default function UseCasePage({ uc }) {
  const { lang, setLang, t } = useLang();
  const c = uc[lang];
  const homeHref = lang === "en" ? "/en/" : "/";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [uc.key]);

  // SEO je Sprache: title, description, canonical, hreflang
  useEffect(() => {
    document.title = c.metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", c.metaDescription);
    setLinkTag("canonical", null, lang === "en" ? `${BASE}/en/${uc.enSlug}` : `${BASE}/${uc.deSlug}`);
    setLinkTag("alternate", "de", `${BASE}/${uc.deSlug}`);
    setLinkTag("alternate", "en", `${BASE}/en/${uc.enSlug}`);
    setLinkTag("alternate", "x-default", `${BASE}/${uc.deSlug}`);
    return () => {
      setLinkTag("alternate", "de", `${BASE}/`);
      setLinkTag("alternate", "en", `${BASE}/en/`);
      setLinkTag("alternate", "x-default", `${BASE}/`);
    };
  }, [lang, uc, c]);

  const others = [...USECASES.filter((o) => o.key !== uc.key)].sort((a, b) => (a.key === MOTHER_KEY ? -1 : b.key === MOTHER_KEY ? 1 : 0));

  return (
    <div className="bg-[#0F1114] text-white min-h-screen" data-testid={`usecase-${uc.key}`}>
      {/* Slim Header */}
      <header className="fixed top-0 inset-x-0 z-50 bg-[#0F1114]/85 backdrop-blur border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-14 h-16 flex items-center justify-between">
          <Link
            to={homeHref}
            translate="no"
            className="notranslate inline-flex items-center gap-2 font-display font-semibold tracking-[0.24em] text-sm text-white"
            data-testid="usecase-brand-link"
          >
            <BrandWordmark lineClass="bg-white/80" />
          </Link>
          <div className="flex items-center gap-4">
            <div className="flex items-center border border-white/15 rounded-full overflow-hidden text-[11px] font-display tracking-[0.2em]">
              <button
                onClick={() => setLang("de")}
                data-testid="lang-de"
                className={`px-3 py-1.5 transition-colors ${lang === "de" ? "bg-white text-[#0F1114]" : "text-white/60 hover:text-white"}`}
              >
                DE
              </button>
              <button
                onClick={() => setLang("en")}
                data-testid="lang-en"
                className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-white text-[#0F1114]" : "text-white/60 hover:text-white"}`}
              >
                EN
              </button>
            </div>
            <a
              href={`${homeHref}#contact`}
              data-testid="usecase-nav-cta"
              className="hidden md:inline-flex px-5 py-2 bg-[#F5D77A] text-[#1F2328] font-medium text-sm tracking-wide hover:bg-white transition-colors"
            >
              {lang === "de" ? "Gespräch anfragen" : "Request meeting"}
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-14 pt-32 md:pt-40 pb-24 md:pb-32">
        {/* Kopf */}
        <Reveal className="max-w-4xl">
          <Link
            to={homeHref}
            data-testid="usecase-back-home"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-white/45 hover:text-white transition-colors font-display"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
            {lang === "de" ? "Startseite" : "Home"}
          </Link>
          <div className="mt-10 flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-white/45 mb-8 font-display">
            <span className="h-px w-8 bg-white/40" />
            {c.kicker || (lang === "de" ? "Anwendung" : "Use case")}
          </div>
          <h1
            className="font-display font-light tracking-[-0.03em] leading-[1.02] text-white"
            style={{ fontSize: "clamp(2.4rem, 5.5vw, 4.8rem)" }}
            data-testid="usecase-h1"
          >
            {c.h1}
          </h1>
          {c.lead && (
            <p
              className="mt-6 font-display italic text-[#F5D77A]/90 tracking-tight"
              style={{ fontSize: "clamp(1.25rem, 2.2vw, 1.9rem)" }}
              data-testid="usecase-lead"
            >
              {c.lead}
            </p>
          )}
          <p
            className="mt-8 text-white/70 font-display font-light tracking-[-0.005em] leading-[1.4] max-w-3xl"
            style={{ fontSize: "clamp(1.05rem, 1.5vw, 1.45rem)" }}
          >
            {c.intro}
          </p>
          {c.notice && (
            <p
              className="mt-8 max-w-3xl text-[13px] md:text-sm leading-relaxed text-white/60 border-l-2 border-[#F5D77A]/60 pl-5"
              data-testid="usecase-notice"
            >
              {c.notice}
            </p>
          )}
        </Reveal>

        {/* Erstes Bild */}
        {c.images[0] && (
          <Reveal className="mt-14 md:mt-20">
            <figure>
              <div className="relative w-full aspect-[4/3] md:aspect-[16/9] overflow-hidden border border-white/10">
                <img src={c.images[0].src} alt={c.images[0].alt || c.images[0].caption} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                <Badge type={c.images[0].type} />
              </div>
              <p className="mt-3 text-[13px] leading-relaxed text-white/50 max-w-3xl">{c.images[0].caption}</p>
            </figure>
          </Reveal>
        )}

        {/* Textabschnitte */}
        <div className="mt-16 md:mt-24 space-y-14 md:space-y-20 max-w-3xl">
          {c.sections.map((s, i) => (
            <Reveal key={i} className={s.type === "compare" || s.type === "audiences" ? "lg:w-[140%] lg:max-w-[calc(1400px-7rem)]" : ""}>
              {s.type === "compare" ? (
                <CompareSection s={s} />
              ) : s.type === "audiences" ? (
                <AudiencesSection s={s} lang={lang} />
              ) : s.type === "proof" ? (
                <ProofSection s={s} />
              ) : (
                <>
                  <h2 className={h2Cls} style={h2Size}>{s.h2}</h2>
                  <p className="mt-5 text-white/65 leading-[1.7] text-[15px] md:text-base">{s.body}</p>
                </>
              )}
            </Reveal>
          ))}
        </div>

        {/* Weitere Bilder */}
        {c.images.length > 1 && (
          <div className="mt-16 md:mt-24 grid md:grid-cols-2 gap-8 md:gap-5">
            {c.images.slice(1).map((img, i) => (
              <Reveal key={img.src} delay={i * 0.08}>
                <figure>
                  <div className="relative w-full aspect-[4/3] overflow-hidden border border-white/10">
                    <img src={img.src} alt={img.alt || img.caption} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    <Badge type={img.type} />
                  </div>
                  <p className="mt-3 text-[13px] leading-relaxed text-white/50">{img.caption}</p>
                </figure>
              </Reveal>
            ))}
          </div>
        )}

        {/* FAQ */}
        {c.faq && c.faq.length > 0 && (
          <Reveal className="mt-20 md:mt-28 border-t border-white/10 pt-10">
            <h2
              className="font-display font-light tracking-[-0.02em] leading-[1.1] text-white"
              style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.3rem)" }}
              data-testid="usecase-faq-title"
            >
              {lang === "de" ? "Häufige Fragen" : "Frequently asked questions"}
            </h2>
            <div className="mt-8 divide-y divide-white/10 border-b border-white/10 max-w-3xl">
              {c.faq.map((f, i) => (
                <details key={i} className="group py-5" data-testid={`usecase-faq-${i}`}>
                  <summary className="flex items-start justify-between gap-6 cursor-pointer list-none text-white/85 hover:text-white transition-colors font-display font-light text-lg md:text-xl tracking-[-0.01em]">
                    {f.q}
                    <Plus className="mt-1.5 h-4 w-4 shrink-0 text-white/40 transition-transform group-open:rotate-45" strokeWidth={1.5} />
                  </summary>
                  <p className="mt-4 text-white/65 leading-[1.7] text-[15px] md:text-base pr-10">{f.a}</p>
                </details>
              ))}
            </div>
          </Reveal>
        )}

        {/* Weitere Anwendungen */}
        <Reveal className="mt-20 md:mt-28 border-t border-white/10 pt-10">
          <div className="text-[11px] tracking-[0.35em] uppercase text-white/45 mb-6 font-display">
            {lang === "de" ? "Weitere Anwendungen" : "More use cases"}
          </div>
          <div className="flex flex-col gap-3">
            {others.map((r) => (
              <Link
                key={r.key}
                to={lang === "en" ? `/en/${r.enSlug}` : `/${r.deSlug}`}
                data-testid={`usecase-related-${r.key}`}
                className="group inline-flex items-center gap-3 text-white/75 hover:text-[#F5D77A] transition-colors w-fit font-display font-light text-lg md:text-xl tracking-[-0.01em]"
              >
                {r[lang].h1}
                <ArrowUpRight className="h-4 w-4 opacity-50 transition-all group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
              </Link>
            ))}
            <Link
              to="/breitscheidplatz"
              data-testid="usecase-related-breitscheidplatz"
              className="group inline-flex items-center gap-3 text-white/75 hover:text-[#F5D77A] transition-colors w-fit font-display font-light text-lg md:text-xl tracking-[-0.01em]"
            >
              {BREITSCHEIDPLATZ_LINK[lang]}
              <ArrowUpRight className="h-4 w-4 opacity-50 transition-all group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </Link>
          </div>
        </Reveal>

        {/* CTAs */}
        <Reveal className="mt-16 md:mt-20">
          <div className="flex flex-col sm:flex-row flex-wrap gap-4">
            {uc.guidePrimary ? (
              <>
                <a
                  href={`${homeHref}?anliegen=foerderleitfaden#contact`}
                  data-testid="usecase-cta-guide"
                  className="group inline-flex items-center justify-between gap-10 px-6 py-4 bg-[#F5D77A] text-[#1F2328] font-medium tracking-wide hover:bg-white transition-colors"
                >
                  <span>{t.financing.funding.guideCta}</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  href={`${homeHref}#contact`}
                  data-testid="usecase-cta-contact"
                  className="group inline-flex items-center justify-between gap-10 px-6 py-4 border border-white/25 text-white font-medium tracking-wide hover:border-[#F5D77A] hover:text-[#F5D77A] transition-colors"
                >
                  <span>{lang === "de" ? "Gespräch anfragen" : "Request meeting"}</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </>
            ) : (
              <>
                <a
                  href={`${homeHref}#contact`}
                  data-testid="usecase-cta-contact"
                  className="group inline-flex items-center justify-between gap-10 px-6 py-4 bg-[#F5D77A] text-[#1F2328] font-medium tracking-wide hover:bg-white transition-colors"
                >
                  <span>{lang === "de" ? "Gespräch anfragen" : "Request meeting"}</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  href={`${homeHref}?anliegen=foerderleitfaden#contact`}
                  data-testid="usecase-cta-guide"
                  className="group inline-flex items-center justify-between gap-10 px-6 py-4 border border-white/25 text-white font-medium tracking-wide hover:border-[#F5D77A] hover:text-[#F5D77A] transition-colors"
                >
                  <span>{t.financing.funding.guideCta}</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </>
            )}
          </div>
        </Reveal>
      </main>

      <Footer />
    </div>
  );
}
